<script setup lang="ts">
import type { Slot } from 'vue'
import CallDetailsMetadataRow, {
  type CallDetailsMetadataRow as CallDetailsMetadataRowData,
} from './CallDetailsMetadataRow.vue'

export type { CallDetailsMetadataRow } from './CallDetailsMetadataRow.vue'

const props = withDefaults(
  defineProps<{
    title?: string
    rows?: CallDetailsMetadataRowData[]
    collapsedRows?: number
    cardClass?: string
    gapClass?: string
    titleTestid?: string
  }>(),
  {
    title: undefined,
    rows: () => [],
    collapsedRows: undefined,
    cardClass: undefined,
    gapClass: 'gap-l',
    titleTestid: undefined,
  },
)

const slots = defineSlots<{
  header?: Slot
  actions?: Slot
  footer?: Slot
}>()

const { t } = useI18n({ useScope: 'global' })
const expanded = ref(false)
const disclosureId = `call-details-metadata-${useId()}`

const collapsedRowsCount = computed(() =>
  props.collapsedRows === undefined
    ? props.rows.length
    : Math.min(Math.max(props.collapsedRows, 0), props.rows.length),
)
const visibleRows = computed((): typeof props.rows => props.rows.slice(0, collapsedRowsCount.value))
const overflowRows = computed((): typeof props.rows => props.rows.slice(collapsedRowsCount.value))
const hasOverflow = computed(
  () => props.collapsedRows !== undefined && overflowRows.value.length > 0,
)
const hasTitleRow = computed(() => Boolean(props.title) || Boolean(slots.actions))
const hasMetadata = computed(() => visibleRows.value.length > 0 || hasOverflow.value)
</script>

<template>
  <div
    class="flex w-full flex-col items-start radius-xl border border-neutral-5 bg-neutral-6 px-xl py-l [border-width:0.5px]"
    :class="[gapClass, cardClass]"
  >
    <slot name="header" />

    <div v-if="hasTitleRow" class="flex w-full items-center justify-between min-h-6 gap-s">
      <span
        v-if="title"
        class="min-w-0 typography-body-1-strong text-neutral-1"
        :data-testid="titleTestid || null"
      >
        <OOverflowTooltip :content="title" class="min-w-0">
          {{ title }}
        </OOverflowTooltip>
      </span>
      <slot name="actions" />
    </div>

    <div
      v-if="hasMetadata"
      class="flex w-full flex-col text-neutral-1"
      data-testid="call-details-metadata"
    >
      <CallDetailsMetadataRow v-for="row in visibleRows" :key="row.id" :row="row" />

      <div
        v-if="hasOverflow"
        class="grid transition-[grid-template-rows] duration-[180ms] ease-[ease] motion-reduce:transition-none"
        :class="expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
      >
        <div
          :id="disclosureId"
          class="overflow-hidden"
          data-testid="call-details-metadata-expanded"
        >
          <div class="flex flex-col gap-xs pt-xs">
            <CallDetailsMetadataRow v-for="row in overflowRows" :key="row.id" :row="row" />
          </div>
        </div>
      </div>

      <button
        v-if="hasOverflow"
        type="button"
        class="group relative isolate mt-xs inline-flex cursor-pointer self-start items-center typography-body-2-link text-primary-interactive underline decoration-solid decoration-from-font [text-underline-position:from-font] focus:outline-none focus-visible:ring-3 focus-visible:ring-(--state-brand-focus) active:!ring-0"
        :aria-expanded="expanded"
        :aria-controls="disclosureId"
        data-testid="call-details-metadata-toggle"
        @click="expanded = !expanded"
      >
        <span
          class="pointer-events-none absolute inset-0 z-0 bg-primary-interactive opacity-0 transition-opacity duration-150 group-hover:opacity-12 group-focus-visible:opacity-16 group-active:opacity-12"
          aria-hidden="true"
        />
        <span class="relative z-1">
          {{ t(expanded ? 'see_less' : 'see_more') }}
        </span>
      </button>
    </div>

    <slot name="footer" />
  </div>
</template>
